import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Pagination,
  InputAdornment,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import SupplierMap from './SupplierMap';
import MatchScore from './MatchScore';
import SupplierBadges from './SupplierBadges';
import { supplierCategories, getSupplierCategoryLabel } from '../../config/supplierTaxonomy';
import useThemeStyles from '../../theme/useThemeStyles';
import {
  MagnifyingGlass,
  Funnel,
  MapPin,
  GridFour,
  Heart,
  Eye,
  Envelope
} from '@phosphor-icons/react';

const ITEMS_PER_PAGE = 10;

const SupplierSearch = () => {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { _user, wedding } = useAuth();
  
  // Main state
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [location, setLocation] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState(10);
  
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [availabilityDate, setAvailabilityDate] = useState(null);
  
  // UI state
  const [showMapView, setShowMapView] = useState(false);
  const [shortlistedSuppliers, setShortlistedSuppliers] = useState([]);
  const [searchLocation, setSearchLocation] = useState(null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [sortBy, setSortBy] = useState(wedding?._id ? 'match' : 'alphabetical'); // 'alphabetical', 'distance', 'match'
  const [locationGeocodeTimeout, setLocationGeocodeTimeout] = useState(null);
  
  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

    // Filter and sort suppliers based on selected criteria
  const sortedSuppliers = useMemo(() => {
    if (!suppliers.length) return [];
    
    let filtered = [...suppliers];
    
    // Apply location filtering if we have search location
    if (searchLocation && searchLocation.coordinates) {
      const [refLng, refLat] = searchLocation.coordinates;
      const maxRadius = radius; // Use the radius from state
      
      filtered = filtered.filter(supplier => {
        if (!supplier.location || !supplier.location.coordinates) {
          return false; // Exclude suppliers without location
        }
        
        const [sup_lng, sup_lat] = supplier.location.coordinates;
        const distance = calculateDistance(refLat, refLng, sup_lat, sup_lng);
        return distance <= maxRadius;
      });
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'match':
        // Sort by match score (highest first), then by name
        return filtered.sort((a, b) => {
          const aScore = a.matchScore !== undefined ? a.matchScore : 0;
          const bScore = b.matchScore !== undefined ? b.matchScore : 0;
          if (bScore !== aScore) return bScore - aScore;
          return (a.businessName || '').localeCompare(b.businessName || '');
        });
      
      case 'alphabetical':
        return filtered.sort((a, b) => a.businessName.localeCompare(b.businessName));
      
      case 'distance':
        // Get reference location (search location or user location)
        let refLat, refLng;
        if (searchLocation && searchLocation.coordinates) {
          [refLng, refLat] = searchLocation.coordinates;
        } else if (userLocation) {
          refLat = userLocation.lat;
          refLng = userLocation.lng;
        } else {
          // No reference location, return as-is
          return filtered;
        }
        
        return filtered.sort((a, b) => {
          if (!a.location || !a.location.coordinates || !b.location || !b.location.coordinates) {
            return 0;
          }
          
          const [a_lng, a_lat] = a.location.coordinates;
          const [b_lng, b_lat] = b.location.coordinates;
          
          const distanceA = calculateDistance(refLat, refLng, a_lat, a_lng);
          const distanceB = calculateDistance(refLat, refLng, b_lat, b_lng);
          
          return distanceA - distanceB;
        });
      
      default:
        return filtered;
    }
  }, [suppliers, sortBy, searchLocation, userLocation, radius]);
  
  // Categories
  const categories = [{ value: '', label: 'All Categories' }, ...supplierCategories];

  // Price range filter removed - pricing is now handled by match scoring

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
          page,
          limit: ITEMS_PER_PAGE,
          search: searchTerm,
        category: selectedCategory,
        minRating: minRating,
        availabilityDate: availabilityDate ? availabilityDate.toISOString().split('T')[0] : null,
        radius: radius
      };
      
      // Add wedding ID for match scoring if available
      if (wedding?._id) {
        params.weddingId = wedding._id;
      }
      
      // Location filtering is handled in frontend for better performance
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await api.get('/api/supplier/search', { params });
      setSuppliers(response.data.suppliers || []);
      setTotalPages(Math.ceil((response.data.total || 0) / ITEMS_PER_PAGE));
    } catch (error) {
      setError(`Failed to fetch suppliers: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch shortlisted suppliers
  const fetchShortlistedSuppliers = async () => {
    try {
      const response = await api.get('/api/user-suppliers');
      setShortlistedSuppliers(
        response.data
          .filter(us => us.status === 'shortlisted' && us.supplier && us.supplier._id)
          .map(us => us.supplier._id.toString())
      );
    } catch (error) {}
  };

  // Get user location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (_error) => {},
        {
          timeout: 10000,
          enableHighAccuracy: false,
          maximumAge: 300000 // 5 minutes
        }
      );
    }
  };

  // Handle search
  const handleSearch = async (e) => {
    e.preventDefault();
    setPage(1);
    
    // Geocode location if provided
    if (location.trim()) {
      try {
        const response = await api.post('/api/geocode/address', { address: location });
        if (response.data.success) {
          setSearchLocation({
            coordinates: response.data.data.coordinates,
            formattedAddress: response.data.data.formattedAddress
          });
        }
      } catch (error) {
        setSearchLocation(null);
      }
    } else {
      setSearchLocation(null);
    }
    
    await fetchSuppliers();
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    switch (filterName) {
      case 'category':
        setSelectedCategory(value);
        break;
      case 'minRating':
        setMinRating(value);
        break;
      case 'availabilityDate':
        setAvailabilityDate(value);
        break;
      case 'radius':
        setRadius(value);
        break;
      default:
        break;
    }
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setLocation('');
    setMinRating(0);
    setAvailabilityDate(null);
    setRadius(10);
    setPage(1);
  };

  // Toggle shortlist
  const toggleShortlist = async (supplierId) => {
    try {
      const supplierIdStr = supplierId?.toString() || supplierId;
      const isShortlisted = shortlistedSuppliers.some(id => id.toString() === supplierIdStr);
      
      if (isShortlisted) {
        // Find the UserSupplier record to get its _id
        const response = await api.get('/api/user-suppliers');
        const userSupplier = response.data.find(
          us => us.supplier && us.supplier._id && us.supplier._id.toString() === supplierIdStr
        );
        
        if (userSupplier) {
          await api.delete(`/api/user-suppliers/${userSupplier._id}`);
        }
      } else {
        // Add to shortlist
        await api.post('/api/user-suppliers', { supplierId: supplierIdStr, status: 'shortlisted' });
      }
      
      // Refresh shortlisted suppliers after toggle
      await fetchShortlistedSuppliers();
    } catch (error) {}
  };


  // Wedding-esque color scheme for business types
  const getCategoryColor = (category) => {
    const colors = {
      venue: '#8B4B8C',        // Deep purple - elegant venues
      photographer: '#D4AF37', // Gold - premium photography
      videographer: '#C9A96E', // Champagne - sophisticated videography
      caterer: '#E8B4B8',      // Blush pink - romantic catering
      florist: '#90EE90',      // Light green - fresh florals
      cake: '#FFB6C1',         // Light pink - sweet cakes
      dj: '#9370DB',           // Medium purple - vibrant entertainment
      band: '#FF6347',         // Tomato - energetic live music
      makeup: '#F0E68C',       // Khaki - natural beauty
      hair: '#DDA0DD',         // Plum - elegant styling
      dress: '#FFC0CB',        // Pink - bridal fashion
      suit: '#708090',         // Slate gray - formal menswear
      transport: '#4682B4',    // Steel blue - reliable transport
      planner: '#D2691E',      // Chocolate - professional planning
      staffing: '#5F9EA0',     // Cadet blue - team coordination
      celebrant_officiant: '#8FBC8F', // Dark sea green - ceremony services
      stationery: '#BC8F8F',   // Rosy brown - printed design
      furniture_hire: '#B0C4DE', // Light steel blue - event hire
      lighting_av: '#6A5ACD',  // Slate blue - production lighting
      default: '#6B7280'       // Gray - fallback color
    };
    return colors[category] || colors.default;
  };

  // Get description preview (75 characters max)
  const getDescriptionPreview = (description) => {
    if (!description) return 'No description available.';
    return description.length > 75 ? description.substring(0, 75) + '...' : description;
  };

  // Get address preview (city and postcode only) - hide private addresses
  const getAddressPreview = (address, addressType) => {
    if (!address || addressType === 'private') {
      // Try to extract city from address even if private
      if (address && addressType === 'private') {
        const parts = address.split(',').map(part => part.trim());
        // Look for city (usually second to last part before postcode)
        if (parts.length >= 2) {
          const city = parts[parts.length - 2];
          return `Based in ${city}`;
        }
      }
      return 'Location not specified';
    }
    
    // Split by comma and get the last parts (usually city and postcode)
    const parts = address.split(',').map(part => part.trim());
    if (parts.length >= 2) {
      // Return last two parts (city and postcode)
      return parts.slice(-2).join(', ');
    }
    return address;
  };

  // Fetch location suggestions from Google Places (villages, towns, cities only)
  const fetchLocationSuggestions = async (query) => {
    if (query.trim().length < 2) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    try {
      const response = await api.get('/api/geocode/places', {
        params: { 
          input: query,
          types: '(cities)' // Only cities, towns, villages, regions
        }
      });

      if (response.data.success) {
        const suggestions = response.data.data.predictions
          .filter(prediction => {
            const desc = prediction.description.toLowerCase();
            return (desc.includes('uk') || desc.includes('united kingdom')) &&
                   !desc.includes('street') &&
                   !desc.includes('road') &&
                   !desc.includes('avenue') &&
                   !desc.includes('lane') &&
                   !desc.includes('close') &&
                   !desc.includes('drive') &&
                   !desc.includes('way') &&
                   !desc.includes('place') &&
                   !desc.includes('crescent') &&
                   !desc.includes('grove') &&
                   !desc.includes('gardens') &&
                   !desc.includes('terrace') &&
                   !desc.includes('square') &&
                   !desc.includes('court') &&
                   !desc.includes('mews') &&
                   !desc.includes('yard') &&
                   !desc.includes('park') &&
                   !desc.includes('house') &&
                   !desc.includes('flat') &&
                   !desc.includes('apartment') &&
                   !desc.includes('building') &&
                   !desc.includes('unit') &&
                   !desc.includes('suite');
          })
          .map(prediction => ({
            label: prediction.description,
            placeId: prediction.place_id
          }));
        setLocationSuggestions(suggestions);
        setShowLocationSuggestions(true);
      }
    } catch (error) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  // Effects
  // Get user location once on mount
  useEffect(() => {
    getUserLocation();
  }, []); // Empty dependency array - only run once on mount

  // Fetch suppliers and shortlisted suppliers when filters change
  useEffect(() => {
    fetchSuppliers();
    fetchShortlistedSuppliers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [page, searchTerm, selectedCategory, minRating, availabilityDate, radius, userLocation, wedding?._id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (locationGeocodeTimeout) {
        clearTimeout(locationGeocodeTimeout);
      }
    };
  }, [locationGeocodeTimeout]);

    return (
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            Find Your Perfect Suppliers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Discover amazing wedding suppliers in your area
          </Typography>
        </Box>
        {/* Search and Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center">
              {/* Location Filter - First */}
              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={locationSuggestions}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                  onInputChange={(event, newInputValue) => {
                    setLocation(newInputValue);
                    
                    // Clear existing timeout
                    if (locationGeocodeTimeout) {
                      clearTimeout(locationGeocodeTimeout);
                    }
                    
                    // Only fetch suggestions if input is long enough and not empty
                    if (newInputValue && newInputValue.length >= 2) {
                      fetchLocationSuggestions(newInputValue);
                      
                      // Set a timeout to geocode the location after user stops typing
                      const timeout = setTimeout(async () => {
                        try {
                          const response = await api.post('/api/geocode/address', { address: newInputValue.trim() });
                          if (response.data.success) {
                            const { formattedAddress } = response.data.data;
                            // Only use the result if it's in the UK
                            if (formattedAddress.toLowerCase().includes('uk') || 
                                formattedAddress.toLowerCase().includes('united kingdom') ||
                                formattedAddress.toLowerCase().includes('england') ||
                                formattedAddress.toLowerCase().includes('scotland') ||
                                formattedAddress.toLowerCase().includes('wales') ||
                                formattedAddress.toLowerCase().includes('northern ireland')) {
                              setSearchLocation({
                                coordinates: response.data.data.coordinates,
                                formattedAddress: response.data.data.formattedAddress
                              });
                            } else {}
                          }
                        } catch (error) {}
                      }, 1000); // Wait 1 second after user stops typing
                      
                      setLocationGeocodeTimeout(timeout);
                    } else {
                      setLocationSuggestions([]);
                      setShowLocationSuggestions(false);
                      setSearchLocation(null); // Clear search location if input is too short
                    }
                  }}
                  onKeyDown={async (event) => {
                    // If user presses Enter, geocode the typed location
                    if (event.key === 'Enter' && location && location.trim()) {
                      event.preventDefault();
                      try {
                        const response = await api.post('/api/geocode/address', { address: location.trim() });
                        if (response.data.success) {
                          const { formattedAddress } = response.data.data;
                          // Only use the result if it's in the UK
                          if (formattedAddress.toLowerCase().includes('uk') || 
                              formattedAddress.toLowerCase().includes('united kingdom') ||
                              formattedAddress.toLowerCase().includes('england') ||
                              formattedAddress.toLowerCase().includes('scotland') ||
                              formattedAddress.toLowerCase().includes('wales') ||
                              formattedAddress.toLowerCase().includes('northern ireland')) {
                            setSearchLocation({
                              coordinates: response.data.data.coordinates,
                              formattedAddress: response.data.data.formattedAddress
                            });
                            setShowLocationSuggestions(false);
                          } else {}
                        }
                      } catch (error) {}
                    }
                  }}
                  onChange={async (event, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      setLocation(newValue.label);
                      setShowLocationSuggestions(false);
                      
                      // Geocode the selected location immediately to update the map
                      try {
                        const response = await api.post('/api/geocode/address', { address: newValue.label });
                        if (response.data.success) {
                          setSearchLocation({
                            coordinates: response.data.data.coordinates,
                            formattedAddress: response.data.data.formattedAddress
                          });
                        }
                      } catch (error) {
                        setSearchLocation(null);
                      }
                    }
                  }}
                  onClose={() => setShowLocationSuggestions(false)}
                  open={showLocationSuggestions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      placeholder="City, postcode, or area"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapPin size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Typography variant="body2">
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </Grid>

              {/* Category Filter - Second */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Search Input - Third */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search suppliers"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search Supplier by name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MagnifyingGlass size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Search Button - Icon Only */}
              <Grid item xs={12} md={1}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ height: '56px', minWidth: 'auto', px: 2 }}
                >
                  <MagnifyingGlass size={20} />
                </Button>
              </Grid>

              {/* Advanced Filters Toggle - Icon Only */}
              <Grid item xs={12} md={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  sx={{ height: '56px', minWidth: 'auto', px: 2 }}
                >
                  <Funnel size={20} />
                </Button>
              </Grid>
            </Grid>
          </form>

          {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Minimum Rating</InputLabel>
                    <Select
                      value={minRating}
                      onChange={(e) => handleFilterChange('minRating', e.target.value)}
                      label="Minimum Rating"
                    >
                      <MenuItem value={0}>Any Rating</MenuItem>
                      <MenuItem value={3}>3+ Stars</MenuItem>
                      <MenuItem value={4}>4+ Stars</MenuItem>
                      <MenuItem value={4.5}>4.5+ Stars</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Available Date"
                    type="date"
                    value={availabilityDate ? availabilityDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => handleFilterChange('availabilityDate', e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Search Radius</InputLabel>
                    <Select
                      value={radius}
                      onChange={(e) => handleFilterChange('radius', e.target.value)}
                      label="Search Radius"
                    >
                      <MenuItem value={5}>5 miles</MenuItem>
                      <MenuItem value={10}>10 miles</MenuItem>
                      <MenuItem value={25}>25 miles</MenuItem>
                      <MenuItem value={50}>50 miles</MenuItem>
                      <MenuItem value={100}>100 miles</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={clearFilters}>
                  Clear All Filters
                </Button>
                <Button variant="contained" onClick={fetchSuppliers}>
                  Apply Filters
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>
        {/* View Toggle */}
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
        }}>
          <Typography variant="h6" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: { xs: '1rem', sm: undefined } }}>
            {sortedSuppliers.length} suppliers found
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 },
            alignItems: 'stretch',
            width: { xs: '100%', sm: 'auto' },
          }}>
            {wedding?._id && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort by"
                >
                  <MenuItem value="match">Best Match</MenuItem>
                  <MenuItem value="alphabetical">Alphabetical</MenuItem>
                  <MenuItem value="distance">Distance</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              variant={showMapView ? "outlined" : "contained"}
              startIcon={showMapView ? <GridFour size={20} /> : <MapPin size={20} />}
              onClick={() => setShowMapView(!showMapView)}
              sx={{ minWidth: { xs: 'auto', sm: undefined } }}
            >
              {showMapView ? 'Grid view' : 'Map view'}
            </Button>
            {!wedding?._id && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="alphabetical">Alphabetical</MenuItem>
                  <MenuItem value="distance">Distance</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {/* Grid View */}
        {!loading && !showMapView && (
          <Grid container spacing={3}>
            {sortedSuppliers.map((supplier) => (
              <Grid item xs={12} sm={6} md={4} key={supplier._id}>
                <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                  {/* Cover Photo */}
                  <Box sx={{ position: 'relative', width: '100%', height: 180 }}>
                    {supplier.coverImage ? (
                      <CardMedia
                        component="img"
                        sx={{ width: '100%', height: 180, objectFit: 'cover' }}
                        image={supplier.coverImage}
                        alt={`${supplier.businessName} cover`}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 180,
                          backgroundColor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No Cover Photo
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Favorite Button - Overlapping Cover Image */}
                    <IconButton
                      size="small"
                      onClick={() => toggleShortlist(supplier._id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(4px)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                        boxShadow: 1
                      }}
                    >
                      <Heart 
                        size={20} 
                        weight={shortlistedSuppliers.some(id => id.toString() === (supplier._id?.toString() || supplier._id)) ? 'fill' : 'regular'}
                        color={shortlistedSuppliers.some(id => id.toString() === (supplier._id?.toString() || supplier._id)) ? '#ef4192' : undefined}
                      />
                    </IconButton>
                  </Box>

                  {/* Supplier Info */}
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, minHeight: 0 }}>
                    {/* Header with Logo and Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      {/* Logo */}
                      {supplier.imageUrl ? (
                        <Box
                          component="img"
                          src={supplier.imageUrl}
                          alt={`${supplier.businessName} logo`}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            objectFit: 'cover',
                            border: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            backgroundColor: 'grey.300',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            No Logo
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Name and Match Score */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            sx={{ 
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '1rem'
                            }}
                          >
                            {supplier.businessName}
                          </Typography>
                          {(supplier.matchScore !== undefined && supplier.matchScore !== null) && (
                            <MatchScore score={supplier.matchScore} breakdown={supplier.matchBreakdown} size="small" />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Category, Location, and Badges */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5, alignItems: 'flex-start', width: '100%', minWidth: 0, flex: '0 0 auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: getCategoryColor(supplier.category) }}>
                          {getSupplierCategoryLabel(supplier.category)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapPin size={14} />
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getAddressPreview(supplier.address, supplier.addressType)}
                          </Typography>
                        </Box>
                      </Box>
                      {supplier.badges && supplier.badges.length > 0 && (
                        <Box sx={{ flex: '0 0 auto', width: '100%', minWidth: 0 }}>
                        <SupplierBadges badges={supplier.badges} />
                        </Box>
                      )}
                    </Box>

                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                      <Rating value={Math.min(Math.max(Number(supplier.rating) || 0, 0), 5)} precision={0.1} size="small" readOnly />
                      <Typography variant="caption" color="text.secondary">
                        ({supplier.reviewCount || 0})
                      </Typography>
                    </Box>

                    {/* Description */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minHeight: 0,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getDescriptionPreview(supplier.description)}
                    </Typography>
                    
                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <Button
                        variant="contained"
                        size="medium"
                        fullWidth
                        startIcon={<Eye size={18} />}
                        onClick={() => navigate(`/dashboard/supplier/${supplier._id}`)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          fontWeight: 500,
                          py: 1.25,
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        fullWidth
                        startIcon={<Envelope size={18} />}
                        onClick={() => navigate(`/chat?supplier=${supplier._id}`)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          fontWeight: 500,
                          py: 1.25,
                        }}
                      >
                        Message
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
        )}
        {/* Map View - 50/50 Split */}
        {!loading && showMapView && (
          <Box sx={{ height: '70vh', display: 'flex', gap: 2 }}>
            {/* Left Side - Supplier List */}
            <Box sx={{ width: '40%', overflow: 'auto', pr: 1 }}>
              <Grid container spacing={2}>
                {sortedSuppliers.map((supplier) => (
                  <Grid item xs={12} key={supplier._id}>
                    <Card sx={{ minHeight: 120 }}>
                      <CardContent sx={{ p: 2 }}>
                        {/* Top Row: Image and Business Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          {/* Supplier Image */}
                          {supplier.imageUrl ? (
                            <CardMedia
                              component="img"
                              sx={{ width: 80, height: 80, objectFit: 'cover' }}
                              image={supplier.imageUrl}
                              alt={supplier.businessName}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              backgroundColor: 'grey.300',
                              display: supplier.imageUrl ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              No Image
                            </Typography>
                          </Box>

                          {/* Business Info */}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, flex: 1 }}>
                                {supplier.businessName}
                              </Typography>
                              {(supplier.matchScore !== undefined && supplier.matchScore !== null) && (
                                <MatchScore score={supplier.matchScore} breakdown={supplier.matchBreakdown} size="small" />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: getCategoryColor(supplier.category) }}>
                                {getSupplierCategoryLabel(supplier.category)}
                              </Typography>
                              {supplier.badges && supplier.badges.length > 0 && (
                                <SupplierBadges badges={supplier.badges} />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Rating value={Math.min(Math.max(Number(supplier.rating) || 0, 0), 5)} precision={0.1} size="small" readOnly />
                              <Typography variant="caption" color="text.secondary">
                                ({supplier.reviewCount || 0} reviews)
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Description Row */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {getDescriptionPreview(supplier.description)}
        </Typography>
                        </Box>
                        
                        {/* Actions Row */}
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button 
            variant="contained" 
                            size="small"
                            startIcon={<Eye size={20} />}
                            onClick={() => navigate(`/dashboard/supplier/${supplier._id}`)}
                          >
                            View
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Envelope size={20} />}
                            onClick={() => navigate(`/chat?supplier=${supplier._id}`)}
                          >
                            Message
          </Button>
                          <IconButton
                            size="small"
                            onClick={() => toggleShortlist(supplier._id)}
                            color={shortlistedSuppliers.some(id => id.toString() === (supplier._id?.toString() || supplier._id)) ? 'error' : 'default'}
                          >
                            <Heart 
                            size={24} 
                            weight={shortlistedSuppliers.some(id => id.toString() === (supplier._id?.toString() || supplier._id)) ? 'fill' : 'regular'}
                          />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
        </Box>

            {/* Right Side - Map */}
            <Box sx={{ width: '60%' }}>
              <SupplierMap
                suppliers={sortedSuppliers}
                shortlistedSuppliers={shortlistedSuppliers}
                onToggleShortlist={toggleShortlist}
                onViewProfile={(supplierId) => navigate(`/dashboard/supplier/${supplierId}`)}
                onMessage={(supplierId) => navigate(`/chat?supplier=${supplierId}`)}
                getCategoryColor={getCategoryColor}
                searchLocation={searchLocation}
              />
            </Box>
          </Box>
        )}
        {/* No Results */}
        {!loading && suppliers.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No suppliers found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or filters
            </Typography>
          </Box>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={totalPages} 
            page={page} 
              onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
        )}
      </Box>
    );
};

export default SupplierSearch;
